import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Fingerprint, X } from 'lucide-react';
import { toast } from 'sonner';

const SEEN_KEY = 'slimmy_bio_prompt_seen';

/**
 * One-shot prompt that invites the user to enable biometric login
 * right after their first authenticated session. Strategy:
 *  - render once per device (flag in localStorage so a logout/login
 *    cycle on the same device doesn't nag again)
 *  - only on Capacitor native AND when biometry hardware is enrolled
 *  - skipped if the user already enabled biometric login earlier
 *  - shown 2s after the user lands on the authed app, so it doesn't
 *    fight the splash fade-out / dashboard reveal
 */
export default function BiometricEnablePrompt() {
  const { user, loading } = useAuth();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [biometry, setBiometry] = useState({ supported: false, label: 'Biometria' });
  const [busy, setBusy] = useState(false);

  // Run detection once user is known + logged in.
  useEffect(() => {
    if (loading || !user) return;
    let mounted = true;
    (async () => {
      try {
        const seen = localStorage.getItem(SEEN_KEY);
        if (seen === '1') return;
        const bio = await import('../lib/biometric');
        const already = await bio.isBiometricLoginEnabled();
        if (already) {
          // No need to ask — they're already set up. Mark as seen
          // so we don't trip the check on every cold start.
          try { localStorage.setItem(SEEN_KEY, '1'); } catch (_) { /* noop */ }
          return;
        }
        const avail = await bio.checkBiometryAvailability();
        if (!avail.available) return;
        if (!mounted) return;
        setBiometry({ supported: true, label: avail.label || 'Biometria' });
        // Small delay so the dashboard renders first.
        setTimeout(() => mounted && setOpen(true), 1800);
      } catch (_) { /* swallow */ }
    })();
    return () => { mounted = false; };
  }, [loading, user]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch (_) { /* noop */ }
    setOpen(false);
  }, []);

  const handleEnable = useCallback(async () => {
    setBusy(true);
    try {
      const bio = await import('../lib/biometric');
      const res = await bio.enableBiometricLogin();
      if (res.success) {
        toast.success(t('profile.biometricEnabled', { type: biometry.label }));
        dismiss();
      } else {
        const isCancel = ['userCancel', 'systemCancel', 'appCancel'].includes(res.error);
        if (!isCancel) {
          toast.error(`${t('profile.biometricEnableFailed')} [${res.error || ''}]`);
        }
        // Re-arm: don't mark as seen so the prompt re-appears next
        // launch unless the user explicitly says "Non ora".
      }
    } catch (e) {
      toast.error(t('profile.biometricEnableFailed'));
    } finally {
      setBusy(false);
    }
  }, [biometry.label, t, dismiss]);

  if (!open || !biometry.supported) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-24 bg-black/60 backdrop-blur-sm">
      <div
        className={`relative w-full max-w-sm rounded-2xl p-6 shadow-2xl ${
          isDark ? 'bg-[#0D1424] text-white' : 'bg-white text-gray-900'
        }`}
        data-testid="biometric-prompt-modal"
      >
        <button
          onClick={dismiss}
          className={`absolute top-3 right-3 p-1 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
          data-testid="biometric-prompt-close"
          aria-label="Chiudi"
        >
          <X className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E8334A] to-[#FF6B82] flex items-center justify-center mb-4 shadow-lg shadow-[#E8334A]/30">
            <Fingerprint className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold mb-2">
            {t('biometricPrompt.title', { type: biometry.label })}
          </h2>
          <p className={`text-sm mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {t('biometricPrompt.body', { type: biometry.label })}
          </p>

          <button
            onClick={handleEnable}
            disabled={busy}
            data-testid="biometric-prompt-yes"
            className="w-full py-3 rounded-xl bg-[#E8334A] text-white font-semibold shadow-md shadow-[#E8334A]/30 disabled:opacity-50 active:scale-[0.98] transition mb-2"
          >
            {busy ? t('common.loading', 'Attendere...') : t('biometricPrompt.enable', { type: biometry.label })}
          </button>
          <button
            onClick={dismiss}
            disabled={busy}
            data-testid="biometric-prompt-no"
            className={`w-full py-2.5 text-sm rounded-xl ${
              isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {t('biometricPrompt.notNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
